import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import {
  WRITE_OFF_REASONS,
  writeOffInventoryBatch,
  type StockLot,
  type WriteOffReason,
} from '../../lib/api';
import { apiErrorMessage } from '../../lib/api';
import { lotName } from './lot-bits';

/**
 * Write off part or all of ONE stock lot (the expired batch, not whichever lot
 * FIFO would pick). A one-line write-off report through the same route the
 * Write off button uses, so it lands in Batches and in the books like any other.
 */
export function LotWriteOffDialog({
  lot,
  onOpenChange,
}: {
  /** The lot to write off; null closes the dialog. */
  lot: StockLot | null;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<WriteOffReason>('expired');
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (!lot) return;
    setQty(String(lot.qtyRemaining));
    setReason(lot.bucket === 'expired' ? 'expired' : 'damaged');
    setMemo('');
  }, [lot]);

  const n = Number(qty);
  const valid = !!lot && qty.trim() !== '' && Number.isInteger(n) && n > 0 && n <= lot.qtyRemaining;

  const submit = useMutation({
    mutationFn: () =>
      writeOffInventoryBatch(lot!.branchId, {
        lines: [
          {
            productId: lot!.productId,
            variantId: lot!.variantId ?? undefined,
            qty: n,
            lotId: lot!.id,
          },
        ],
        reason,
        memo: memo.trim() || `Lot ${lotName(lot!)}`,
      }),
    meta: { successMessage: 'Lot written off.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-stock-lots'] });
      qc.invalidateQueries({ queryKey: ['admin-inventory', lot!.branchId] });
      qc.invalidateQueries({ queryKey: ['admin-inventory-batches', lot!.branchId] });
      qc.invalidateQueries({ queryKey: ['admin-inventory-logs', lot!.branchId] });
      onOpenChange(false);
    },
  });

  const unit = lot?.unitLabel?.trim() || 'pcs';

  return (
    <Dialog open={!!lot} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Write off lot</DialogTitle>
          <DialogDescription>
            {lot
              ? `${lot.productName}${lot.variantName ? ` (${lot.variantName})` : ''} · ${lotName(lot)} · ${lot.branchName}`
              : ''}
          </DialogDescription>
        </DialogHeader>
        {lot && (
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="lot-writeoff-qty" className="text-xs text-muted-foreground">
                Quantity ({lot.qtyRemaining.toLocaleString()} {unit} left in this lot)
              </label>
              <Input
                id="lot-writeoff-qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Reason</span>
              <div className="flex flex-wrap gap-2">
                {WRITE_OFF_REASONS.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => setReason(r.code)}
                    className={`rounded-md border px-3 py-1 text-sm ${
                      reason === r.code
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label htmlFor="lot-writeoff-memo" className="text-xs text-muted-foreground">
                Memo (optional)
              </label>
              <Input
                id="lot-writeoff-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                maxLength={300}
              />
            </div>
            {submit.isError && (
              <p className="text-xs text-destructive">
                {apiErrorMessage(submit.error, 'Could not write off this lot.')}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!valid || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Saving…' : 'Write off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
